import React, { useState, useEffect } from "react";
import { ContactSection } from "./ContactSection";
import { UpdateContactModal } from "./UpdateContactModal";
import axios from "axios";

export function Contacts() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [opportunityName, setOpportunityName] = useState('');
  const [contactSections, setContactSections] = useState({
    customerContacts: {
      title: "Nettles Project Team",
      contacts: [
        { title: "Operations Manager", contacts: [] },
        { title: "Project Manager", contacts: [] },
        { title: "Project Accountant", contacts: [] },
        { title: "Submittals", contacts: [] },
        { title: "Salesperson", contacts: [] }
      ]
    },
    gcCustomer: {
      title: "GC/Customer",
      contacts: [
        { title: "Project Manager", contacts: [] },
        { title: "Asst. Project Manager", contacts: [] },
        { title: "Superintendent", contacts: [] },
        { title: "Project Engineer", contacts: [] },
        { title: "Billing Contact", contacts: [] }
      ]
    },
    Vendor: {
      title: "Vendors",
      contacts: [
        { contacts: [] } // No title for vendors
      ]
    },
    Design: {
      title: "Designer/Consultant",
      contacts: [
        { title: "Architect", contacts: [] },
        { title: "Engineer", contacts: [] },
        { title: "Inspector", contacts: [] },
        { title: "Consultant", contacts: [] }
      ]
    }
  });
  
  // Add function to fetch contacts
  const fetchContacts = async (opportunityUUID) => {
    try {
      const response = await axios.get(`https://projects-dev.nettlescs.com:5000/api/contacts/opportunity/${opportunityUUID}`);
      const transformedData = transformApiData(response.data);
      setContactSections(transformedData);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  useEffect(() => {
    // Get project details including opportunity name from localStorage
    const projectData = localStorage.getItem('selectedProjectDetails');
    if (projectData) {
      try {
        const parsed = JSON.parse(projectData);
        setOpportunityName(parsed.opportunityName);
        fetchContacts(parsed.opportunityUUID);
      } catch (e) {
        console.error('Error parsing project data:', e);
      }
    }
  }, []);

  useEffect(() => {
    const handleOpenModal = (event) => {
      setSelectedSection(event.detail.sectionType);
      setIsModalOpen(true);
    };

    window.addEventListener('openContactModal', handleOpenModal);
    return () => window.removeEventListener('openContactModal', handleOpenModal);
  }, []);

  // Handle modal close and refresh
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedSection(null);
    
    // Refresh contacts data
    const projectData = localStorage.getItem('selectedProjectDetails');
    if (projectData) {
      try {
        const parsed = JSON.parse(projectData);
        fetchContacts(parsed.opportunityUUID);
      } catch (e) {
        console.error('Error parsing project data:', e);
      }
    }
  };

  // Helper function to transform API data into our structure
  const transformApiData = (apiData) => {
    if (!apiData?.data) return;

    // First, group and filter contacts to keep only the latest entries
    const latestContacts = {};
    apiData.data.forEach(contact => {
      const key = `${contact.Opportunity_System_Wide_ID__c}-${contact.Group}-${contact.ContactType}-${contact.AccountId}`;
      if (!latestContacts[key] || latestContacts[key].id < contact.id) {
        latestContacts[key] = contact;
      }
    });

    // Initialize the structure with empty contacts arrays
    const sections = {
      customerContacts: {
        title: "Nettles Project Team",
        contacts: [
          { title: "Operations Manager", contacts: [] },
          { title: "Project Manager", contacts: [] },
          { title: "Project Accountant", contacts: [] },
          { title: "Submittals", contacts: [] },
          { title: "Salesperson", contacts: [] }
        ]
      },
      gcCustomer: {
        title: "GC/Customer",
        contacts: [
          { title: "Project Manager", contacts: [] },
          { title: "Asst. Project Manager", contacts: [] },
          { title: "Superintendent", contacts: [] },
          { title: "Project Engineer", contacts: [] },
          { title: "Billing Contact", contacts: [] }
        ]
      },
      Vendor: {
        title: "Vendors",
        contacts: [
          { contacts: [] } // No title for vendors
        ]
      },
      Design: {
        title: "Designer/Consultant",
        contacts: [
          { title: "Architect", contacts: [] },
          { title: "Engineer", contacts: [] },
          { title: "Inspector", contacts: [] },
          { title: "Consultant", contacts: [] }
        ]
      }
    };

    // Map filtered API data to our structure
    Object.values(latestContacts).forEach(contact => {
      const contactData = {
        name: `${contact.FirstName} ${contact.LastName}`,
        title: contact.GMB_Type__c || '-',
        email: contact.Email || '-',
        phones: [
          contact.Phone || '-',
          contact.MobilePhone || '-'
        ],
        // Disable chat for Nettles Project Team
        hasChat: contact.Group == "Nettles Project Team"
      };

      // Map Group to our section keys
      const sectionMap = {
        "Nettles Project Team": "customerContacts",
        "GC/Customer": "gcCustomer",
        "Vendors": "Vendor",
        "Designer/Consultant": "Design"
      };

      const sectionKey = sectionMap[contact.Group];
      if (!sectionKey) return;

      if (contact.Group === "Vendors") {
        sections[sectionKey].contacts[0].contacts.push(contactData);
      } else {
        const section = sections[sectionKey].contacts.find(
          s => s.title === contact.ContactType
        );
        if (section) {
          section.contacts.push(contactData);
        }
      }
    });

    return sections;
  };

  return (
    <div className="flex flex-col px-5 pt-4 pb-9 mx-auto w-full rounded-xl bg-stone-50">
      <div className="flex justify-between items-center">
        <div className="text-xl font-semibold text-zinc-800">
          Contacts
        </div>
      </div>
      <div className="mt-4">
        <ContactSection {...contactSections.customerContacts} />
        <div className="mt-3">
          <ContactSection {...contactSections.gcCustomer} />
        </div>
        <div className="mt-3">
          <ContactSection {...contactSections.Vendor} />
        </div>
        <div className="mt-3">
          <ContactSection {...contactSections.Design} />
        </div>
      </div>

      <UpdateContactModal 
        isOpen={isModalOpen} 
        onClose={handleModalClose}
        sectionType={selectedSection}
        opportunityName={opportunityName}
      />
    </div>
  );
}
